import { useCallback, useRef, useState } from "react";

/**
 * Function signature for fetch operations that support cancellation.
 * @template T - The expected return type of the fetch operation
 * @param signal - AbortSignal used to cancel the request
 * @param args - Additional arguments to pass to the fetch function
 */
type FetchFn<T> = (signal: AbortSignal, ...args: any[]) => Promise<T>;

/**
 * Result object returned by the runFetch function.
 * @template T - The type of the data returned on success
 */
type FetchResult<T> = {
  /** Whether the fetch operation completed successfully */
  success: boolean;
  /** The fetched data if successful, null otherwise */
  data: T | null;
  /** Error object if the fetch failed, null otherwise */
  error: any;
};

/**
 * Optional behavior for an abortable fetch instance.
 */
interface UseAbortableFetchOptions {
  /** Clears the previous successful response when a non-abort request fails. */
  clearDataOnError?: boolean;
}

/**
 * Per-call options for {@link useAbortableFetch}'s `runFetch`.
 * Pass as the last argument; it is not forwarded to the fetch function.
 */
export interface AbortableFetchCallOptions {
  /**
   * When true, this request does not set the hook's `loading` flag.
   * Defaults to false, so loaders still show.
   */
  hideLoader?: boolean;
}

/**
 * Returns call options when the last `runFetch` argument opts out of the loader.
 *
 * @param value - Candidate last argument from `runFetch`.
 * @returns The options object when it carries `hideLoader`; otherwise undefined.
 */
const readHideLoaderOptions = (
  value: unknown,
): AbortableFetchCallOptions | undefined => {
  if (
    typeof value !== "object" ||
    value === null ||
    Array.isArray(value) ||
    !("hideLoader" in value)
  ) {
    return undefined;
  }

  return value as AbortableFetchCallOptions;
};

/**
 * A React hook for making abortable fetch requests.
 *
 * This hook provides automatic request cancellation when a new request is made,
 * preventing race conditions and stale data. It also exposes loading, error,
 * and data states for convenient UI integration.
 *
 * @template T - The expected return type of the fetch operation
 * @param fetchFn - A function that accepts an AbortSignal as its first parameter and returns a Promise
 * @param options - Optional error-handling behavior for this fetch instance
 * @returns An object containing:
 *   - `runFetch`: Function to trigger the fetch operation
 *   - `abort`: Function to manually cancel the current request
 *   - `reset`: Cancels the request and clears data, error, and loading state
 *   - `data`: The fetched data (cleared on errors only when `clearDataOnError` is true)
 *   - `error`: The error object if the fetch failed
 *   - `loading`: Boolean indicating if a fetch is in progress
 *
 * @example
 * ```tsx
 * const { runFetch, data, loading, error } = useAbortableFetch(
 *   (signal) => fetchUserData(signal, userId)
 * );
 *
 * const handleClick = async () => {
 *   const result = await runFetch();
 *   if (result.success) {
 *     console.log('Data fetched:', result.data);
 *   }
 * };
 * ```
 */
export function useAbortableFetch<T = any>(
  fetchFn: FetchFn<T>,
  options: UseAbortableFetchOptions = {},
) {
  const abortControllerRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  /** Cancel the current request and discard all retained fetch state. */
  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  const runFetch = useCallback(
    async (...args: any[]): Promise<FetchResult<T>> => {
      const callOptions = readHideLoaderOptions(args[args.length - 1]);
      const fetchArgs = callOptions ? args.slice(0, -1) : args;
      const hideLoader = callOptions?.hideLoader ?? false;

      abortControllerRef.current?.abort();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (!hideLoader) {
        setLoading(true);
      }
      setError(null);

      try {
        const result = await fetchFn(controller.signal, ...fetchArgs);
        // A cancelled request may still resolve if its fetcher ignores the signal.
        if (
          controller.signal.aborted ||
          abortControllerRef.current !== controller
        ) {
          return { success: false, data: null, error: null };
        }
        setData(result);

        return {
          success: true,
          data: result,
          error: null,
        };
      } catch (err: any) {
        if (
          err.name !== "AbortError" &&
          !controller.signal.aborted &&
          abortControllerRef.current === controller
        ) {
          setError(err);
          if (options.clearDataOnError) {
            setData(null);
          }
        }

        return {
          success: false,
          data: null,
          error: err,
        };
      } finally {
        if (abortControllerRef.current === controller) {
          setLoading(false);
        }
      }
    },
    [fetchFn, options.clearDataOnError],
  );
  return { runFetch, abort, reset, data, error, loading };
}
